// @flow
import { Platform } from 'react-native';
import config from '../config';
import { reportError, reportCrashLog } from './analytics';

// Console output is disabled in production.
// - Native builds: config.env is set to 'prod' by the CI pipeline (src/config/env.js).
// - Web builds: environment is resolved dynamically at runtime via window.WIZZEE_DYNAMIC_CONFIG.
// Crashlytics reporting (warn/error levels) is always active on native, regardless of env.
const _isProd = (() => {
  if (config.env === 'prod') return true;
  try {
    // $FlowFixMe
    return typeof window !== 'undefined' &&
      window.WIZZEE_DYNAMIC_CONFIG != null &&
      window.WIZZEE_DYNAMIC_CONFIG.ENV === 'prod';
  } catch {
    return false;
  }
})();

const CONSOLE_ENABLED = !_isProd;

const STYLE = {
  log:   'color:#fff;background:#4A90D9;padding:1px 6px;border-radius:3px;font-weight:bold',
  info:  'color:#fff;background:#27AE60;padding:1px 6px;border-radius:3px;font-weight:bold',
  warn:  'color:#fff;background:#F39C12;padding:1px 6px;border-radius:3px;font-weight:bold',
  error: 'color:#fff;background:#E74C3C;padding:1px 6px;border-radius:3px;font-weight:bold',
  debug: 'color:#fff;background:#8E44AD;padding:1px 6px;border-radius:3px;font-weight:bold',
};

const LABEL = {
  log:   'LOG  ',
  info:  'INFO ',
  warn:  'WARN ',
  error: 'ERROR',
  debug: 'DEBUG',
};

// Maps and Sets are not JSON-serialisable by default; convert them so they
// display correctly as expandable objects in Chrome DevTools / Flipper.
const normalizeArg = arg => {
  if (arg instanceof Map) return Object.fromEntries(arg);
  if (arg instanceof Set) return Array.from(arg);
  return arg;
};

const _argToStr = a =>
  typeof a === 'string' ? a : JSON.stringify(normalizeArg(a));

// Resolves printf-style specifiers (%s, %d, %i, %f, %o/%O) in the first
// string argument, mirroring how console.log handles format strings.
const argsToString = args => {
  if (!args.length) return '';
  if (typeof args[0] !== 'string') return args.map(_argToStr).join(' ');
  const [fmt, ...rest] = args;
  let i = 0;
  const result = fmt.replace(/%[sdifjoO%]/g, spec => {
    if (spec === '%%') return '%';
    const val = rest[i++];
    if (val === undefined) return spec;
    if (spec === '%d' || spec === '%i') return String(parseInt(val, 10));
    if (spec === '%f') return String(parseFloat(val));
    if (spec === '%o' || spec === '%O') return JSON.stringify(normalizeArg(val));
    return String(val);
  });
  const leftover = rest.slice(i);
  return leftover.length ? `${result} ${leftover.map(_argToStr).join(' ')}` : result;
};

const print = (level, consoleFn, args) => {
  if (!CONSOLE_ENABLED) return;
  const prefix = `%c WizzeeLog · ${LABEL[level]} `;
  const [first, ...rest] = args;
  if (typeof first === 'string') {
    // Append the first string arg to the format string so the engine resolves
    // printf-style specifiers (%d, %s, %f …) natively, just like console.log.
    consoleFn(prefix + first, STYLE[level], ...rest.map(normalizeArg));
  } else {
    consoleFn(prefix, STYLE[level], ...args.map(normalizeArg));
  }
};

export default class WizzeeLog {
  // Generic log (equivalent to console.log)
  static log = (...args) => print('log', console.log, args);

  // Informational message (equivalent to console.info)
  static info = (...args) => print('info', console.info, args);

  // Verbose / development-only (use console.log internally so it doesn't
  // trigger the Metro yellow-box for non-warning messages)
  static debug = (...args) => print('debug', console.log, args);

  // Warning — console output (native: yellow Metro banner) + Crashlytics breadcrumb
  static warn = (...args) => {
    print('warn', console.warn, args);
    if (Platform.OS !== 'web') {
      reportCrashLog(`[WARN] ${argsToString(args)}`);
    }
  };

  // Error — console output (native: red Metro banner) + Crashlytics non-fatal error
  static error = (...args) => {
    print('error', console.error, args);
    if (Platform.OS !== 'web') {
      const err =
        args.find(a => a instanceof Error) ||
        new Error(argsToString(args));
      reportError(err);
    }
  };
}

